<template>
  <div class="dashboard-filter-container">
    <div class="filter-group multiselect-wrap--small">
      <label>{{ $t('OPPORTUNITY_DASHBOARD.FILTERS.PIPELINE') }}</label>
      <multiselect
        v-model="selectedPipeline"
        class="no-margin"
        track-by="id"
        label="name"
        :placeholder="$t('OPPORTUNITY_DASHBOARD.FILTERS.SELECT_PIPELINE')"
        :multiple="false"
        selected-label
        :select-label="$t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
        deselect-label=""
        :close-on-select="true"
        :options="pipelines"
        :allow-empty="true"
        :disabled="disabled"
      />
    </div>

    <div class="filter-group">
      <label>{{ $t('OPPORTUNITY_DASHBOARD.FILTERS.PERIOD_FROM') }}</label>
      <input v-model="filters.createdFrom" type="date" :disabled="disabled" />
    </div>

    <div class="filter-group">
      <label>{{ $t('OPPORTUNITY_DASHBOARD.FILTERS.PERIOD_TO') }}</label>
      <input v-model="filters.createdTo" type="date" :disabled="disabled" />
    </div>

    <div class="filter-group multiselect-wrap--small">
      <label>{{ $t('OPPORTUNITY_DASHBOARD.FILTERS.STATUS') }}</label>
      <multiselect
        v-model="selectedStatus"
        class="no-margin"
        track-by="id"
        label="name"
        :placeholder="$t('OPPORTUNITY_DASHBOARD.FILTERS.ALL')"
        :multiple="false"
        selected-label
        :select-label="$t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
        deselect-label=""
        :close-on-select="true"
        :options="statusOptions"
        :allow-empty="false"
        :disabled="disabled"
      />
    </div>

    <div class="filter-group multiselect-wrap--small">
      <label>{{ $t('OPPORTUNITY_DASHBOARD.FILTERS.OWNER') }}</label>
      <multiselect
        v-model="selectedOwner"
        class="no-margin"
        track-by="id"
        label="name"
        :placeholder="$t('OPPORTUNITY_DASHBOARD.FILTERS.ALL')"
        :multiple="false"
        selected-label
        :select-label="$t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
        deselect-label=""
        :close-on-select="true"
        :options="ownerOptions"
        :allow-empty="false"
        :disabled="disabled"
      />
    </div>

    <div class="filter-group multiselect-wrap--small">
      <label>{{ $t('OPPORTUNITY_DASHBOARD.FILTERS.INBOX') }}</label>
      <multiselect
        v-model="selectedInbox"
        class="no-margin"
        track-by="id"
        label="name"
        :placeholder="$t('OPPORTUNITY_DASHBOARD.FILTERS.ALL')"
        :multiple="false"
        selected-label
        :select-label="$t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
        deselect-label=""
        :close-on-select="true"
        :options="inboxOptions"
        :allow-empty="false"
        :disabled="disabled"
      />
    </div>

    <div class="filter-group filter-group--buttons">
      <label class="filter-group__spacer" aria-hidden="true" />
      <div class="button-wrapper">
        <woot-button :is-disabled="disabled" @click="applyFilters">
          {{ $t('OPPORTUNITY_DASHBOARD.FILTERS.APPLY') }}
        </woot-button>
        <woot-button
          color-scheme="secondary"
          :is-disabled="disabled"
          @click="clearFilters"
        >
          {{ $t('OPPORTUNITY_DASHBOARD.FILTERS.CLEAR') }}
        </woot-button>
      </div>
    </div>
  </div>
</template>

<script>
import { DEFAULT_DASHBOARD_FILTERS as DEFAULT_FILTERS } from 'dashboard/helper/opportunityDashboardHelper';
import {
  withAllOption,
  findFilterOption,
  translatedFilterOptions,
} from 'dashboard/helper/opportunityFilterHelper';

const STATUSES = ['open', 'won', 'lost', 'canceled'];

export default {
  props: {
    pipelines: { type: Array, default: () => [] },
    agents: { type: Array, default: () => [] },
    inboxes: { type: Array, default: () => [] },
    initialFilters: { type: Object, default: () => ({}) },
    disabled: { type: Boolean, default: false },
  },
  data() {
    return {
      filters: { ...DEFAULT_FILTERS(), ...this.initialFilters },
    };
  },
  computed: {
    allLabel() {
      return this.$t('OPPORTUNITY_DASHBOARD.FILTERS.ALL');
    },
    statusOptions() {
      return withAllOption(
        this.allLabel,
        translatedFilterOptions(STATUSES, this.statusOptionLabel)
      );
    },
    ownerOptions() {
      return withAllOption(this.allLabel, this.agents);
    },
    inboxOptions() {
      return withAllOption(this.allLabel, this.inboxes);
    },
    selectedStatus: {
      get() {
        return findFilterOption(this.statusOptions, this.filters.status);
      },
      set(option) {
        this.filters.status = option ? option.id : null;
      },
    },
    selectedPipeline: {
      get() {
        return (
          this.pipelines.find(
            pipeline => pipeline.id === this.filters.pipelineId
          ) || null
        );
      },
      set(pipeline) {
        this.filters.pipelineId = pipeline ? pipeline.id : null;
      },
    },
    selectedOwner: {
      get() {
        return findFilterOption(this.ownerOptions, this.filters.ownerId);
      },
      set(agent) {
        this.filters.ownerId = agent ? agent.id : null;
      },
    },
    selectedInbox: {
      get() {
        return findFilterOption(this.inboxOptions, this.filters.inboxId);
      },
      set(inbox) {
        this.filters.inboxId = inbox ? inbox.id : null;
      },
    },
  },
  watch: {
    'initialFilters.pipelineId': function watchInitialPipelineId(pipelineId) {
      if (pipelineId && !this.filters.pipelineId) {
        this.filters.pipelineId = pipelineId;
      }
    },
  },
  methods: {
    statusOptionLabel(status) {
      return this.$t(
        `OPPORTUNITY_DETAILS.HISTORY_EVENTS.STATUS_VALUES.${status}`
      );
    },
    applyFilters() {
      this.$emit('filter-applied', { ...this.filters });
    },
    clearFilters() {
      this.filters = {
        ...DEFAULT_FILTERS(),
        pipelineId: this.filters.pipelineId,
      };
      this.applyFilters();
    },
  },
};
</script>

<style scoped lang="scss">
.dashboard-filter-container {
  align-items: flex-end;
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-normal);
  margin-bottom: var(--space-normal);
}

.filter-group {
  display: flex;
  flex-direction: column;
  min-width: 12rem;

  label {
    font-size: var(--font-size-mini);
    margin-bottom: var(--space-micro);
    line-height: 1.6rem;
  }

  select,
  input {
    height: 4rem;
    margin-bottom: 0;
  }
}

.filter-group--buttons {
  min-width: 0;
}

.filter-group__spacer {
  display: block;
  height: 1.6rem;
  margin-bottom: var(--space-micro);
  visibility: hidden;
}

.button-wrapper {
  display: flex;
  gap: var(--space-smaller);
  height: 4rem;
}
</style>
